/**
 * usePullToRefresh — touch-based pull-to-refresh for any scrollable
 * container. Native-feeling on Android (matches WhatsApp / Gmail
 * elastic-pull semantics).
 *
 * Usage:
 *   const ref = useRef<HTMLDivElement>(null)
 *   const { distance, state } = usePullToRefresh(ref, async () => {
 *     await qc.invalidateQueries({ queryKey: ['chats'] })
 *   })
 *
 *   <div ref={ref} className="overflow-y-auto h-full">
 *     <PullIndicator distance={distance} state={state} />
 *     ...rows...
 *   </div>
 *
 * Behaviour:
 *   - Only engages when the container is scrolled to the very top.
 *   - Pull distance is rubber-banded (sqrt easing) so over-pull feels
 *     elastic, not infinite.
 *   - Trigger threshold: 64px. Below = bounces back. Above = fires the
 *     onRefresh callback and shows a spinner until it resolves.
 *   - On a non-touch device (mouse, desktop) the hook is a no-op so
 *     it doesn't interfere with normal scroll.
 */

import { useEffect, useRef, useState } from 'react'

export type PullState = 'idle' | 'pulling' | 'release' | 'refreshing'

const THRESHOLD = 64        // px to release before triggering a refresh
const MAX_DISPLAYED = 96    // hard cap on indicator travel
const RUBBER_BAND_DIVISOR = 2.5  // higher = stiffer pull


export function usePullToRefresh(
  ref: React.RefObject<HTMLElement | null>,
  onRefresh: () => Promise<unknown>,
): { distance: number; state: PullState } {
  const [distance, setDistance] = useState(0)
  const [state, setState] = useState<PullState>('idle')
  // Stable refs so the gesture handlers see the latest callback / state
  // without re-binding listeners on every render.
  const onRefreshRef = useRef(onRefresh)
  onRefreshRef.current = onRefresh
  const stateRef = useRef<PullState>('idle')
  stateRef.current = state

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof window === 'undefined') return
    // Skip on devices with no touch support — desktop should keep its
    // normal scroll wheel behaviour, no surprises.
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    if (!hasTouch) return

    let startY = 0
    let pulling = false
    let moveAttached = false

    function detachMove() {
      if (moveAttached) {
        el!.removeEventListener('touchmove', onMove)
        moveAttached = false
      }
    }

    function onStart(e: TouchEvent) {
      // Only engage when at the very top of the scroll container.
      if (!el || el.scrollTop > 0) return
      if (stateRef.current === 'refreshing') return
      if (e.touches.length !== 1) return
      startY = e.touches[0].clientY
      pulling = true
      // Attach the non-passive touchmove only for gestures that begin at
      // the top boundary. Every other gesture (mid-list scroll, fast
      // fling) avoids JS-thread gating so the compositor can run the
      // scroll at the panel's native refresh rate.
      el.addEventListener('touchmove', onMove, { passive: false })
      moveAttached = true
    }

    function onMove(e: TouchEvent) {
      if (!pulling || !el) return
      // If the user scrolled the list down (which can happen with
      // momentum), abort the pull gesture cleanly.
      if (el.scrollTop > 0) {
        pulling = false
        setDistance(0)
        setState('idle')
        detachMove()
        return
      }
      const dy = e.touches[0].clientY - startY
      if (dy <= 0) {
        // Pulling up (or no movement) — let normal scroll behaviour
        // win. Detach so the rest of this gesture is uncontested by JS.
        if (stateRef.current !== 'idle') {
          setDistance(0)
          setState('idle')
        }
        detachMove()
        return
      }
      // Rubber-band the pull so it feels elastic.
      const eased = Math.min(
        MAX_DISPLAYED,
        Math.sqrt(dy / RUBBER_BAND_DIVISOR) * 6,
      )
      setDistance(eased)
      setState(eased >= THRESHOLD ? 'release' : 'pulling')
      // Block the browser's native pull-to-refresh / overscroll bounce
      // so the gesture feels like ours, not the browser's.
      if (e.cancelable) e.preventDefault()
    }

    async function onEnd() {
      detachMove()
      if (!pulling) return
      pulling = false
      const trigger = stateRef.current === 'release'
      if (trigger) {
        setState('refreshing')
        // Stay at the threshold while the refresh runs so the spinner
        // is visible.
        setDistance(THRESHOLD)
        try {
          await onRefreshRef.current()
        } catch (e) {
          // Refresh failure shouldn't strand the UI in 'refreshing'
          // state. Log + reset.
          console.warn('pull-to-refresh callback failed', e)
        } finally {
          setState('idle')
          setDistance(0)
        }
      } else {
        setState('idle')
        setDistance(0)
      }
    }

    // touchmove is attached dynamically inside onStart only when the
    // gesture begins at the top boundary — see comment there.
    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchend', onEnd, { passive: true })
    el.addEventListener('touchcancel', onEnd, { passive: true })
    return () => {
      el.removeEventListener('touchstart', onStart)
      el.removeEventListener('touchend', onEnd)
      el.removeEventListener('touchcancel', onEnd)
      detachMove()
    }
  }, [ref])

  return { distance, state }
}


/** Tiny visual indicator. Renders an arrow that flips to a spinner once
 *  past threshold and stays spinning during the refresh.
 *  Inserted as the FIRST child of the scroll container so it's anchored
 *  to the top of the list. */
export function PullIndicator({
  distance, state,
}: {
  distance: number
  state: PullState
}) {
  if (state === 'idle' && distance === 0) return null
  return (
    <div
      className="flex items-center justify-center overflow-hidden text-stone-500 dark:text-stone-400"
      style={{
        height: `${distance}px`,
        transition: state === 'refreshing' ? 'height 200ms ease-out' : undefined,
      }}
      aria-live="polite"
    >
      {state === 'refreshing' ? (
        <SpinningArrow spinning />
      ) : state === 'release' ? (
        <span className="text-[11px] flex items-center gap-1.5 opacity-90">
          <SpinningArrow flipped />
          Release to refresh
        </span>
      ) : (
        <span
          className="text-[11px] flex items-center gap-1.5"
          style={{ opacity: Math.min(1, distance / 40) }}
        >
          <SpinningArrow rotation={(distance / 64) * 180} />
          Pull to refresh
        </span>
      )}
    </div>
  )
}


function SpinningArrow({
  spinning, flipped, rotation,
}: { spinning?: boolean; flipped?: boolean; rotation?: number }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={spinning ? 'animate-spin' : ''}
      style={{
        transform:
          rotation !== undefined ? `rotate(${rotation}deg)` :
          flipped ? 'rotate(180deg)' :
          undefined,
        transition: spinning ? undefined : 'transform 80ms linear',
      }}
    >
      <path d="M8 2v9" />
      <path d="M4 7l4 4 4-4" />
    </svg>
  )
}
